import { useMemo } from "react";
import "../Styles/Card.css";
import { timeAgo } from "../utils/format";
import { CommentIcon } from "./icons/CommentIcon";
import { PlayPauseIcon } from "./icons/PlayPauseIcon";
import type { UserFeed } from "../data/types";
import DefaultAvatar from "./DefaultAvatar";
import TrackCover from "./TrackCover";
import { usePreviewAudio } from "../Hooks/usePreviewAudio";
import LikeButton from "./LikeButton";
import { BASE_URL } from "../data/constants";

type CardProps = {
  feedObj: UserFeed;
};

export default function UserCard({ feedObj }: CardProps) {
  const u = feedObj.user;

  const displayName =
    `${u?.firstName ?? ""} ${u?.lastName ?? ""}`.trim() || "Unknown";

  // Samma väg som kartkortet: previewen spelas genom vår proxy, inte mot
  // källans URL. Saknas den ritas ingen knapp — en spelaknapp som inte
  // spelar något är värre än ingen knapp alls.
  const previewProxyUrl = useMemo(() => {
    if (!feedObj.trackId || !feedObj.hasPreview) return undefined;
    return `${BASE_URL}/api/preview/${feedObj.trackId}`;
  }, [feedObj.trackId, feedObj.hasPreview]);

  const { isPlaying, toggle, failed } = usePreviewAudio(previewProxyUrl);

  return (
    <div className="card-wrapper card-user">
      <div className="card-top">
        <DefaultAvatar avatarUrl={u?.avatarUrl ?? undefined} name={displayName} />

        <div className="group-column">
          <p className="text-b">{displayName}</p>
          <p className="text-damper">{timeAgo(feedObj.time)}</p>
        </div>
      </div>

      <div className="card-center">
        {feedObj.caption && <p className="text">{feedObj.caption}</p>}

        <div className="preview-container-user">
          <TrackCover src={feedObj.albumCover} />

          <div className="group-column card-track-meta">
            <p className="text-l">{feedObj.songTitle}</p>
            <p className="text-damp">{feedObj.artist}</p>
            {feedObj.year && (
              <p className="text-damper margin-top">{feedObj.year}</p>
            )}
          </div>

          {previewProxyUrl && !failed && (
            <button
              className="play-button"
              onClick={toggle}
              aria-label={isPlaying ? "Pause preview" : "Play preview"}
            >
              <PlayPauseIcon size={18} className="play-pause" isPlaying={isPlaying} />
            </button>
          )}
        </div>
      </div>

      <div className="card-bottom">
        <div className="interact-item">
          {/* Bara ett antal. Kommentarer har inget flöde än, och en knapp
              som inte gör något är värre än ingen knapp. */}
          <span className="commentButton comment-static" title="Comments">
            <CommentIcon size={18} className={"comment-svg"} />
          </span>
          <span className="text-damp">{feedObj.comments}</span>
        </div>

        <LikeButton
          postId={feedObj.id}
          likes={feedObj.likes}
          likedByMe={feedObj.likedByMe}
        />
      </div>
    </div>
  );
}
